import { Injectable, inject } from '@angular/core';

import { ConfigStateService, RoutesService, isNullOrUndefined } from '@abp/ng.core';
import { tap } from 'rxjs/operators';

import { createEffect } from '@nexweb/shared/effects';
import { FeatureFlagNames } from '@nexweb/shared/guards/feature-flag';

import {
  etsEnergySettlementMenuName,
  etsFinancialSettlementMenuName,
  etsOperationalReportingMenuName,
  manageDlcSettlementsESetMenuName,
  manageDlcSettlementsFSetMenuName,
  manageTariffSettlementsESetMenuName,
  manageTariffSettlementsFSetMenuName,
  operationalReportingMenuName,
  sappMarketSettlementResultsMenuName,
  settlementsDashboardMenuName,
  settlementsMenuName,
} from './constants';

@Injectable({ providedIn: 'root' })
export class SetrepMenuEffects {
  public configStateService = inject(ConfigStateService);
  public routes = inject(RoutesService);

  operationalReportingMenuEffect$ = createEffect(() =>
    this.configStateService.getFeature$(FeatureFlagNames.operationalReportingEnabled).pipe(
      tap((featureFlag) => {
        if (!isNullOrUndefined(featureFlag)) {
          const featureValue = JSON.parse(featureFlag);

          this.routes.patch(operationalReportingMenuName, {
            invisible: !featureValue,
          });
          this.routes.patch(etsOperationalReportingMenuName, {
            invisible: !featureValue,
          });
        }
      }),
    ),
  );

  energySchedulingMenuEffect$ = createEffect(() =>
    this.configStateService.getFeature$(FeatureFlagNames.energySettlementEnabled).pipe(
      tap((featureFlag) => {
        if (!isNullOrUndefined(featureFlag)) {
          const featureValue = JSON.parse(featureFlag);

          this.routes.patch(etsEnergySettlementMenuName, {
            invisible: !featureValue,
          });
        }
      }),
    ),
  );

  financialSchedulingMenuEffect$ = createEffect(() =>
    this.configStateService.getFeature$(FeatureFlagNames.financialSettlementEnabled).pipe(
      tap((featureFlag) => {
        if (!isNullOrUndefined(featureFlag)) {
          const featureValue = JSON.parse(featureFlag);

          this.routes.patch(etsFinancialSettlementMenuName, {
            invisible: !featureValue,
          });

          this.routes.patch(settlementsMenuName, {
            invisible: !featureValue,
          });
        }
      }),
    ),
  );

  energySettlementTariffMenuEffect$ = createEffect(() =>
    this.configStateService
      .getFeatures$([FeatureFlagNames.energySettlementEnabled, FeatureFlagNames.settlementTariffEnabled])
      .pipe(
        tap((values: Record<string, string> | undefined) => {
          if (!isNullOrUndefined(values)) {
            const esetKey = FeatureFlagNames.energySettlementEnabled;
            const tariffKey = FeatureFlagNames.settlementTariffEnabled;

            if (values && !isNullOrUndefined(values[esetKey]) && !isNullOrUndefined(values[tariffKey])) {
              const energySettlementFeatureValue = JSON.parse(values[esetKey]);
              const tariffFeatureValue = JSON.parse(values[tariffKey]);

              this.routes.patch(manageTariffSettlementsESetMenuName, {
                invisible: !energySettlementFeatureValue || !tariffFeatureValue,
              });
            }
          }
        }),
      ),
  );

  financialSettlementTariffMenuEffect$ = createEffect(() =>
    this.configStateService
      .getFeatures$([FeatureFlagNames.financialSettlementEnabled, FeatureFlagNames.settlementTariffEnabled])
      .pipe(
        tap((values: Record<string, string> | undefined) => {
          if (!isNullOrUndefined(values)) {
            const fsetKey = FeatureFlagNames.financialSettlementEnabled;
            const tariffKey = FeatureFlagNames.settlementTariffEnabled;

            if (values && !isNullOrUndefined(values[fsetKey]) && !isNullOrUndefined(values[tariffKey])) {
              const financialSettlementFeatureValue = JSON.parse(values[fsetKey]);
              const tariffFeatureValue = JSON.parse(values[tariffKey]);

              this.routes.patch(manageTariffSettlementsFSetMenuName, {
                invisible: !financialSettlementFeatureValue || !tariffFeatureValue,
              });
            }
          }
        }),
      ),
  );

  energySettlementDlcMenuEffect$ = createEffect(() =>
    this.configStateService
      .getFeatures$([FeatureFlagNames.energySettlementEnabled, FeatureFlagNames.dealCaptureEnabled])
      .pipe(
        tap((values: Record<string, string> | undefined) => {
          if (!isNullOrUndefined(values)) {
            const esetKey = FeatureFlagNames.energySettlementEnabled;
            const dlcKey = FeatureFlagNames.dealCaptureEnabled;

            if (values && !isNullOrUndefined(values[esetKey]) && !isNullOrUndefined(values[dlcKey])) {
              const energySettlementFeatureValue = JSON.parse(values[esetKey]);
              const dlcFeatureValue = JSON.parse(values[dlcKey]);

              this.routes.patch(manageDlcSettlementsESetMenuName, {
                invisible: !energySettlementFeatureValue || !dlcFeatureValue,
              });
            }
          }
        }),
      ),
  );

  financialSettlementDlcMenuEffect$ = createEffect(() =>
    this.configStateService
      .getFeatures$([FeatureFlagNames.financialSettlementEnabled, FeatureFlagNames.dealCaptureEnabled])
      .pipe(
        tap((values: Record<string, string> | undefined) => {
          if (!isNullOrUndefined(values)) {
            const fsetKey = FeatureFlagNames.financialSettlementEnabled;
            const dlcKey = FeatureFlagNames.dealCaptureEnabled;

            if (values && !isNullOrUndefined(values[fsetKey]) && !isNullOrUndefined(values[dlcKey])) {
              const financialSettlementFeatureValue = JSON.parse(values[fsetKey]);
              const dlcFeatureValue = JSON.parse(values[dlcKey]);

              this.routes.patch(manageDlcSettlementsFSetMenuName, {
                invisible: !financialSettlementFeatureValue || !dlcFeatureValue,
              });
            }
          }
        }),
      ),
  );

  sappMarketResultsMenuEffect$ = createEffect(() =>
    this.configStateService
      .getFeatures$([FeatureFlagNames.financialSettlementEnabled, FeatureFlagNames.etsMtpIntegrationEnabled])
      .pipe(
        tap((values: Record<string, string> | undefined) => {
          if (!isNullOrUndefined(values)) {
            const finSetKey = FeatureFlagNames.financialSettlementEnabled;
            const etsMtpKey = FeatureFlagNames.etsMtpIntegrationEnabled;

            if (values && !isNullOrUndefined(values[finSetKey]) && !isNullOrUndefined(values[etsMtpKey])) {
              const finSetFeatureValue = JSON.parse(values[finSetKey]);
              const etsMtpFeatureValue = JSON.parse(values[etsMtpKey]);

              this.routes.patch(sappMarketSettlementResultsMenuName, {
                invisible: !etsMtpFeatureValue || !finSetFeatureValue,
              });
            }
          }
        }),
      ),
  );

  settlementDashboardMenuEffect$ = createEffect(() =>
    this.configStateService.getFeatures$([FeatureFlagNames.settlementDashboardEnabled]).pipe(
      tap((values: Record<string, string> | undefined) => {
        if (!isNullOrUndefined(values)) {
          const settlementDashboardKey = FeatureFlagNames.settlementDashboardEnabled;

          if (values && !isNullOrUndefined(values[settlementDashboardKey])) {
            const settlementDashboardFeatureValue = JSON.parse(values[settlementDashboardKey]);

            this.routes.patch(settlementsDashboardMenuName, {
              invisible: !settlementDashboardFeatureValue,
            });
          }
        }
      }),
    ),
  );
}
